import { PlusIcon } from '@radix-ui/react-icons'
import {
  createLink,
  Link,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { Button } from 'antd'
import Cantilever from 'assets/cantilever-logo.svg?react'
import { RightSideDrawer } from 'components/rightDrawer/RightSideDrawer'
import { DrawerState } from 'components/rightDrawer/utils/enums'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import {
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PermissionsGate,
} from 'features/auth'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoOrganization, GeckoSite } from 'types'

import DropdownSearch from './DropdownSearch'
import { useNavbarData } from './useNavbarData'

const HeaderSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto; /* Do not grow or shrink, set a fixed height */
  margin-bottom: 20px;
  width: 100%;
  gap: 0.125rem;
`

const CantileverLogo = styled(Cantilever)`
  height: 40px;
  min-width: 40px;
  max-width: 40px;
`

const CantileverTextContainer = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  margin-left: 8px;
`

const StyledLogoLinkStyles = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  text-decoration: none;
  color: ${({ theme }) => theme.forecast.defaultGhostButton};
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  cursor: pointer;

  path {
    fill: ${({ theme }) => theme.forecast.tertiaryText};
    fill-opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};

    path {
      fill: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`
const StyledLogoLink = createLink(StyledLogoLinkStyles)

const StyledLinkDiv = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledButtonDiv = styled.div`
  display: flex;
  justify-content: end;
`

const StyledButton = styled(Button)`
  cursor: pointer;
  font-weight: bold;
  background-color: ${({ theme }) => theme.forecast.defaultButton} !important;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton} !important;
    color: ${({ theme }) => theme.forecast.text1} !important;
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeButton} !important;
  }
`

export const HeaderSection: React.FC<{
  isLeftSidebarOpen: boolean
  siteID: string | undefined
  hideSiteSelector: boolean
}> = ({ isLeftSidebarOpen, siteID, hideSiteSelector }) => {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()

  const matchNoSitesRoute = matchRoute({ to: '/no-sites/$orgID' })
  const matchEditOrgRoute = matchRoute({ to: '/organization/$orgID' })
  const matchEditSiteRoute = matchRoute({
    to: '/organization/$orgID/site/$siteID',
  })

  const orgID = matchNoSitesRoute
    ? matchNoSitesRoute.orgID
    : matchEditOrgRoute
      ? matchEditOrgRoute.orgID
      : matchEditSiteRoute
        ? matchEditSiteRoute.orgID
        : undefined

  const { organizations, organization, site, sites, isLoading, hasEverLoaded } =
    useNavbarData({
      siteID,
      orgID,
    })

  // List of Organizations with their associated Sites
  const orgsWithAssociatedSites = useMemo(
    () =>
      organizations.toSorted(GeckoOrganization.DefaultSort).map((o) => ({
        organization: o,
        sites: sites.filter((s) => s.tags.organization === o.id),
      })),
    [organizations, sites]
  )

  // List of Sites filtered by the current Organization
  const sitesFilteredByOrganization = useMemo(
    () =>
      sites
        .filter((s) => s.tags?.organization === organization?.id)
        .toSorted(GeckoSite.DefaultSort) ?? [],
    [organization, sites]
  )

  const handleSiteSelect = (site: GeckoSite | undefined) => {
    if (!site) return

    // Define logic to match routes and determine if navigation is needed
    // This is based on your router's route configuration
    if (
      matchRoute({ to: '/assets/$siteID/$unitID' }) ||
      matchRoute({ to: '/assets/$siteID' })
    ) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/details/$entryID' })) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (
      matchRoute({ to: '/health-check' }) ||
      matchRoute({ to: '/admin/users' })
    ) {
      navigate({
        to: '.',
      })
    } else if (matchRoute({ to: '/viewer/$unitID/configuration' })) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns/$siteID/$walkdownId' })) {
      // For walkdown details page, navigate to the walkdowns list for the new site
      navigate({
        to: `/walkdowns/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns/$siteID' })) {
      // For walkdowns list page, update the site ID
      navigate({
        to: `/walkdowns/${site.id}`,
        replace: true,
      })
    } else {
      navigate({
        to: '.',
        params: { siteID: site.id },
      })
    }
  }

  const handleOrgSelect = (
    item:
      | {
          organization: GeckoOrganization
          sites: GeckoSite[]
        }
      | undefined
  ) => {
    const site = item?.sites?.at(0)
    const organization = item?.organization

    if (matchRoute({ to: '/users/$orgID' })) {
      navigate({
        to: `/users/${organization?.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/extensions/$orgID' })) {
      navigate({
        to: `/extensions/${organization?.id}`,
        replace: true,
      })
    } else if (site) {
      handleSiteSelect(site)
    } else if (!site && organization?.id) {
      navigate({
        to: `/no-sites/${organization?.id}`,
        replace: true,
      })
    } else {
      navigate({
        to: '.',
        params: { orgID: organization?.id },
        replace: true,
      })
    }
  }

  const { setDrawerState, setDrawerEntry } = useRightDrawer()

  const getEditLink = useCallback(
    (
      classToEdit: GeckoOrganization | GeckoSite,
      closeMenu: () => Promise<void>
    ) => {
      let linkTitle

      const handleEditClick = async (e: React.MouseEvent) => {
        await closeMenu()
        setDrawerEntry(classToEdit)

        if (classToEdit instanceof GeckoOrganization) {
          navigate({
            to: '/organization/$orgID',
            search: { mode: 'edit' },
            params: { orgID: classToEdit.id },
          })
        } else if (classToEdit instanceof GeckoSite) {
          navigate({
            to: '/organization/$orgID/site/$siteID',
            search: { mode: 'edit' },
            params: {
              orgID: classToEdit.tags.organization,
              siteID: classToEdit.id,
            },
          })
        } else {
          setDrawerState(DrawerState.ErrorState)
        }

        e.preventDefault()
      }

      if (classToEdit instanceof GeckoOrganization) {
        linkTitle = 'Edit Org'
      } else if (classToEdit instanceof GeckoSite) {
        linkTitle = 'Edit Site'
      } else {
        linkTitle = 'Edit'
      }

      return (
        <div onClick={handleEditClick}>
          <a className={'editLink'}>{linkTitle}</a>
        </div>
      )
    },
    [setDrawerEntry, setDrawerState, navigate]
  )

  const getAddButton = useCallback(
    (classToAdd: DrawerState, closeMenu: () => Promise<void>) => {
      let title
      if (classToAdd === DrawerState.EditGeckoOrganization) {
        title = 'Add Org'
        return (
          <StyledButtonDiv>
            <StyledButton
              onClick={async (e) => {
                await closeMenu()
                navigate({
                  to: '/organization',
                  search: { mode: 'create' },
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </StyledButton>
          </StyledButtonDiv>
        )
      } else if (classToAdd === DrawerState.EditGeckoSite) {
        title = 'Add Site'
        return (
          <StyledButtonDiv>
            <StyledButton
              onClick={async (e) => {
                await closeMenu()
                navigate({
                  to: '/organization/$orgID/site',
                  params: { orgID: organization?.id ?? '' },
                  search: { mode: 'create' },
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </StyledButton>
          </StyledButtonDiv>
        )
      } else {
        throw new Error(
          `Unexpected getAddButton state! ${DrawerState[classToAdd]}`
        )
      }
    },
    [organization, navigate]
  )

  return (
    <HeaderSectionContainer>
      <StyledLogoLink
        to='/assets/$siteID'
        params={{ siteID: site?.id ?? '' }}
        style={{ textDecoration: 'none' }}>
        <CantileverLogo />
        <CantileverTextContainer>cantilever</CantileverTextContainer>
      </StyledLogoLink>

      <DropdownSearch>
        <DropdownSearch.Trigger
          contentData={organization}
          isLeftSidebarOpen={isLeftSidebarOpen}
          isSingleItem={orgsWithAssociatedSites.length === 1}
          isLoading={(isLoading || !hasEverLoaded) && !organization}
          testId='org-dropdown'
        />
        <DropdownSearch.Menu>
          <DropdownSearch.FilteredSearch
            listToFilter={orgsWithAssociatedSites}
            itemGetter={({ organization }, closeMenu) => (
              <StyledLinkDiv>
                {organization.data.name}
                <PermissionsGate requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                  {getEditLink(organization, closeMenu)}
                </PermissionsGate>
              </StyledLinkDiv>
            )}
            itemSearchTerms={({ organization }) => [
              organization.data.name,
              organization.data.name.replaceAll('.', ''),
              organization.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
            ]}
            onSelect={handleOrgSelect}
            placeholder='Search Organizations(…)'
            extra={({ closeMenu }) => (
              <PermissionsGate requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                {getAddButton(DrawerState.EditGeckoOrganization, closeMenu)}
              </PermissionsGate>
            )}
          />
        </DropdownSearch.Menu>
      </DropdownSearch>

      {!hideSiteSelector && (
        <DropdownSearch>
          <DropdownSearch.Trigger
            contentData={site}
            isLeftSidebarOpen={isLeftSidebarOpen}
            isSingleItem={sitesFilteredByOrganization.length === 1}
            isLoading={(isLoading || !hasEverLoaded) && !site}
          />
          <DropdownSearch.Menu>
            <DropdownSearch.FilteredSearch
              listToFilter={sitesFilteredByOrganization}
              itemGetter={(site, closeMenu) => (
                <StyledLinkDiv>
                  {site.data.name}
                  <PermissionsGate
                    requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                    {getEditLink(site, closeMenu)}
                  </PermissionsGate>
                </StyledLinkDiv>
              )}
              itemSearchTerms={(site) => [
                site.data.name,
                site.data.name.replaceAll('.', ''),
                site.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
              ]}
              onSelect={handleSiteSelect}
              placeholder='Search Sites(…)'
              extra={({ closeMenu }) => (
                <PermissionsGate requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                  {getAddButton(DrawerState.EditGeckoSite, closeMenu)}
                </PermissionsGate>
              )}
            />
          </DropdownSearch.Menu>
        </DropdownSearch>
      )}

      <RightSideDrawer />
    </HeaderSectionContainer>
  )
}
